'use client';

// The change queue status bridge (BL-2438 AC 4, BL-2569, BL-2685). The phone app's offline bar is
// mounted above the signed-in shell (RealtimeProviderMount), but the change queue is read inside it
// (OfflineSyncProvider, which needs the tenant and user). The provider reports its status up here
// from one derivation: the bar shows a waiting indicator, and the sidebar's Storage & Sync entry
// shows, in red, the count of failures, which is the number of failure rows the page lists.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** The queue's status, from one derivation: is anything waiting, and how many failures (BL-2685). */
interface QueueCounts {
  pending: boolean;
  issues: number;
}

interface BridgeValue {
  counts: QueueCounts | null;
  setCounts: (counts: QueueCounts | null) => void;
}

const ChangeQueueCountContext = createContext<BridgeValue | null>(null);

/**
 * Holds the change queue's status for surfaces mounted above the signed-in shell.
 *
 * @param props.children - the bar and the app.
 * @returns the provider.
 */
export function ChangeQueueCountBridge({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<QueueCounts | null>(null);
  const value = useMemo(() => ({ counts, setCounts }), [counts]);
  return <ChangeQueueCountContext.Provider value={value}>{children}</ChangeQueueCountContext.Provider>;
}

/**
 * @returns true while any change waits to sync (and none of it is counted as failed), or null when
 *   the status is unknown (outside the native app, until both queues are read, after a failed read, or with no bridge mounted).
 */
export function useChangeQueuePending(): boolean | null {
  return useContext(ChangeQueueCountContext)?.counts?.pending ?? null;
}

/**
 * @returns the count of failures the page lists (BL-2569, BL-2685), or null when the status is unknown.
 */
export function useChangeQueueIssueCount(): number | null {
  return useContext(ChangeQueueCountContext)?.counts?.issues ?? null;
}

/**
 * Report the queue's status to the bridge; clears them when the reporter unmounts (sign-out, a
 * tenant switch), so no surface keeps a previous session's number.
 *
 * @param counts - pending and the failure count, or null while the queue is not read.
 * @sideEffect Writes the bridge's counts.
 */
export function useReportChangeQueueCounts(counts: QueueCounts | null): void {
  const setCounts = useContext(ChangeQueueCountContext)?.setCounts;
  const pending = counts?.pending ?? null;
  const issues = counts?.issues ?? null;
  useEffect(() => {
    setCounts?.(pending == null || issues == null ? null : { pending, issues });
  }, [setCounts, pending, issues]);
  useEffect(() => () => setCounts?.(null), [setCounts]);
}
